import { AuthAPI } from 'utils/httpAPI';
import { store } from 'store';
import {
  getRefToken,
  jwtDecoder,
  getDecodeAccessToken,
  saveAccessToken,
  saveRefreshToken,
  deleteToken,
} from 'utils/auth/tokenUtils';
import { getSupportRegion } from 'utils/snippet';
import { checkExpireAction } from 'utils/auth/sessionManager';
import * as Sentry from '@sentry/browser';

let tokenUpdateTimer: NodeJS.Timeout;
function tokenUpdateInterval() {
  if (tokenUpdateTimer) {
    clearInterval(tokenUpdateTimer);
  }
  tokenUpdateTimer = setInterval(() => {
    const refToken = getRefToken();
    const refDecodeToken = jwtDecoder(refToken);
    const accDecodeToken = getDecodeAccessToken();
    const currentSec = Math.floor(new Date().getTime() / 1000);
    if (accDecodeToken && accDecodeToken.exp > currentSec + 60) {
      return;
    }

    if (refToken && refDecodeToken && refDecodeToken.exp > currentSec) {
      // token refresh
      wrapperTokenRefresh(refToken);
      return;
    }

    // 두 토큰이 둘다 없을 때 == 당근웹채팅은 껐지만 브라우저는 끄지 않고 1주일 지난 상황
    // login page redirect
    deleteToken();
    // window.location.reload()
  }, 1000 * 60);
}

// 진행 중인 갱신 요청. 동시에 호출되면(React StrictMode 이중 실행, 60초 주기 갱신 등) 같은 요청을 함께 기다린다.
let refreshInFlight: Promise<void> | null = null;
// 갱신 실패를 Sentry에 보고했는지. 주기 갱신이 60초마다 재시도하므로 세션당 한 번만 보고한다.
let refreshFailureReported = false;
// 갱신이 끝날 때까지 기다릴 수 있도록 프로미스를 반환한다.
// 서버가 토큰을 거절하면 안에서 로그인으로 보내고, 전송 오류는 삼켜 다음 진입·주기 갱신에서 다시 시도한다.
async function wrapperTokenRefresh(encodeRefToken: string): Promise<void> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  if (checkExpireAction()) {
    deleteToken();
    window.location.href = '/login';
  }
  const allowRegion = getSupportRegion();
  refreshInFlight = AuthAPI.tokenRefresh(encodeRefToken, allowRegion)
    .then(({ data }) => {
      const token = data.access_token;
      const refToken = data.refresh_token;
      if (token && refToken) {
        saveAccessToken(token);
        saveRefreshToken(refToken);
        store.dispatch({ type: 'user/setAccessToken', payload: token });
        return;
      }
      deleteToken();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    })
    .catch((error) => {
      // 전송 오류는 삼키되, 토큰 없이 앱이 뜨는 세션이 얼마나 생기는지 볼 수 있게 세션당 한 번만 보고한다.
      // Sentry ignoreErrors는 부분 문자열 일치라 axios 메시지를 message에 넣으면 버려지므로 extra로만 보낸다.
      if (refreshFailureReported) return;
      refreshFailureReported = true;
      Sentry.captureMessage('tokenRefresh failed', {
        level: Sentry.Severity.Error,
        tags: { feature: 'token-refresh' },
        extra: { reason: error instanceof Error ? error.message : String(error) },
      });
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export { tokenUpdateInterval, wrapperTokenRefresh };
